import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { CloseButton } from '@sorare/core/src/atoms/buttons/CloseButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandS } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { CommonEssenceIcon } from '@sorare/core/src/components/commonGame/CommonEssenceIcon';
import { GemsBalance } from '@sorare/core/src/components/commonGame/GemsBalance';
import { Dialog } from '@sorare/core/src/components/dialog';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { ANY_SPORT_MY_CARDS_CARDS_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { ensureHexColor } from '@sorare/core/src/lib/colors';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { OpenBuyGemsDialog_Context } from '@sorare/core/src/protos/events/platform/web/events';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RevealPackCardsDialog } from 'components/commonCard/RevealPackCardsDialog';

import type {
  BuyCardPackMutation,
  BuyCardPackMutationVariables,
  PackOpeningDialog_cardPack,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  min-height: 100%;
  padding: var(--double-and-a-half-unit) var(--double-unit) 0;
  background: linear-gradient(
      to bottom,
      transparent 0%,
      transparent 70%,
      var(--secondary-color) 100%
    ),
    linear-gradient(to bottom, transparent 0%, var(--secondary-color) 100%),
    linear-gradient(
      to bottom,
      var(--secondary-color) 0%,
      var(--primary-color) 100%
    );
`;
const Content = styled(Vertical)`
  justify-content: center;
  padding: var(--double-unit);
  padding-bottom: var(--quadruple-unit);
  gap: var(--double-unit);
  flex: 1;

  @media ${laptopAndAbove} {
    gap: var(--quadruple-unit);
    min-height: min(calc(100vh - var(--unit) * 12), 600px);
  }
`;
const Img = styled.img`
  aspect-ratio: var(--card-aspect-ratio);
  width: 65%;
  max-width: 240px;
`;
const StyledLoadingButton = styled(LoadingButton)`
  align-self: center;
  && {
    min-width: 200px;
  }
`;
const Warning = styled(Horizontal)`
  width: 100%;
  padding: var(--intermediate-unit) var(--double-unit);
  background: var(--c-surface-caution);
  border-radius: var(--double-unit);
  justify-content: space-between;
  border: 1px solid var(--c-yellow-400);
`;

const Cost = styled(Horizontal)`
  gap: var(--unit);
  font: var(--t-brand-s);
  text-transform: uppercase;
  color: var(--c-nd-600);
`;

const BUY_CARD_PACK_MUTATION = gql`
  mutation BuyCardPackMutation($input: buyCardPackInput!, $sport: Sport!) {
    buyCardPack(input: $input) {
      currentUser {
        slug
        commonCardPoints(sport: $sport)
        gemsBalances: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [COMMON_GEM]
        ) {
          amount
        }
      }
      cards {
        slug
        ...RevealPackCardsDialog_anyCardInterface
      }
      pack {
        id
        cardPack {
          slug
          contributesToUnlockTask {
            id
            ... on StarPackTask {
              id
              aasmState
              progress
            }
          }
        }
      }
      errors {
        code
        message
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
` as TypedDocumentNode<BuyCardPackMutation, BuyCardPackMutationVariables>;

type Props = {
  open: boolean;
  onClose: () => void;
  cardPack: PackOpeningDialog_cardPack;
  showPlayerDetails?: boolean;
  starCardPackSlug?: string;
};
export const PackOpeningDialog = ({
  open,
  onClose,
  cardPack,
  showPlayerDetails,
  starCardPackSlug,
}: Props) => {
  const { pictureUrl, price, currency } = cardPack;
  const track = useEvents();
  const [buyCardPack, { loading }] = useMutation(BUY_CARD_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
    update: (cache, { data: mutationData }) => {
      const contributesToUnlockTask =
        mutationData?.buyCardPack?.pack?.cardPack?.contributesToUnlockTask;
      if (!contributesToUnlockTask || !starCardPackSlug) {
        return;
      }
      // update star pack rewardedByTask field if it exists
      // this is needed because initially rewardedByTask is a template
      // and the id will change when it becomes a real task
      cache.modify({
        id: cache.identify({
          slug: starCardPackSlug,
          __typename: 'CardPack',
        }),
        fields: {
          rewardedByTask: () =>
            mutationData?.buyCardPack?.pack?.cardPack
              ?.contributesToUnlockTask || undefined,
        },
      });
    },
  });

  const openDialog = useOpenDialog();
  const { sport } = useSportContext();
  const [buyCardPackData, setBuyCardPackData] =
    useState<BuyCardPackMutation['buyCardPack']>();
  const { currentUser } = useCurrentUserContext();

  const premiumPack = currency === 'COMMON_GEM';

  const userBalance = premiumPack
    ? currentUser?.gemsBalances?.[0]?.amount || 0
    : currentUser?.commonCardPoints || 0;

  const getInteractionContext = useGetInteractionContext();
  const { generateSportContextPath } = useSportContext();

  const onPurchase = async () => {
    const { data } = await buyCardPack({
      variables: {
        input: {
          cardPackSlug: cardPack.slug,
          sport,
          interactionContext: getInteractionContext(),
        },
        sport,
      },
    });
    setBuyCardPackData(data?.buyCardPack);
  };
  const packNotBought =
    buyCardPackData?.cards?.length === 0 || !buyCardPackData?.pack?.id;
  const insufficientBalance = packNotBought && userBalance < price;

  return (
    <>
      {packNotBought ? (
        <Dialog
          open={open}
          onClose={onClose}
          hideHeader
          fullWidth
          maxWidth="xs"
          style={
            {
              '--primary-color': cardPack.primaryColor,
              '--secondary-color': cardPack.secondaryColor,
            } as CSSProperties
          }
          disableBackdropClick
        >
          <Root gap={2}>
            <SBHorizontal>
              {premiumPack ? (
                <GemsBalance balance={userBalance} />
              ) : (
                <EssenceBalance value={userBalance} rarity={Rarity.common} />
              )}
              <CloseButton size="medium" onClose={onClose} />
            </SBHorizontal>
            <Content>
              <Vertical center gap={3}>
                <Img
                  src={proxyUrl(pictureUrl, { cropWidth: 320 })}
                  alt=""
                  width={320}
                />
                <Cost color="var(--c-nd-600)">
                  <FormattedMessage
                    id="PackOpeningDialog.cost"
                    defaultMessage="Cost: {currencyIcon} <span>{cost}</span>"
                    values={{
                      cost: price,
                      currencyIcon: premiumPack ? (
                        <GemIcon size={2.5} />
                      ) : (
                        <CommonEssenceIcon width={18} />
                      ),
                      span: chunks => (
                        <BrandS color="var(--c-white)">{chunks}</BrandS>
                      ),
                    }}
                  />
                </Cost>
              </Vertical>
              <Vertical center gap={2.5}>
                <StyledLoadingButton
                  color="primary"
                  size="medium"
                  loading={loading}
                  onClick={onPurchase}
                  disabled={insufficientBalance}
                >
                  <FormattedMessage {...glossary.buyNow} />
                </StyledLoadingButton>
                {!!insufficientBalance && (
                  <Warning>
                    <BodyM color="var(--c-yellow-400)" bold>
                      <Horizontal>
                        <FontAwesomeIcon icon={faInfoCircle} />
                        <FormattedMessage
                          id="commonPaymentBox.insufficientBalance"
                          defaultMessage="Insufficient balance"
                        />
                      </Horizontal>
                    </BodyM>
                    {premiumPack ? (
                      <Button
                        size="medium"
                        color="secondary"
                        onClick={() => {
                          track('Open Buy Gems Dialog', {
                            context: OpenBuyGemsDialog_Context.PACK_DIALOG,
                          });
                          openDialog(DialogKey.buyGems, 'true');
                        }}
                      >
                        <FormattedMessage
                          id="commonPaymentBox.buyGems"
                          defaultMessage="Buy gems"
                        />
                      </Button>
                    ) : (
                      <Button
                        size="medium"
                        color="secondary"
                        to={generateSportContextPath(
                          ANY_SPORT_MY_CARDS_CARDS_TYPE,
                          {
                            params: {
                              type: Rarity.common,
                            },
                          }
                        )}
                      >
                        <FormattedMessage
                          id="commonPaymentBox.scrapCard"
                          defaultMessage="Scrap Card"
                        />
                      </Button>
                    )}
                  </Warning>
                )}
              </Vertical>
            </Content>
          </Root>
        </Dialog>
      ) : (
        <RevealPackCardsDialog
          open={open}
          onClose={onClose}
          cards={buyCardPackData!.cards!}
          onContinue={onClose}
          packId={buyCardPackData!.pack!.id!}
          packPictureUrl={pictureUrl}
          primaryColor={ensureHexColor(cardPack.primaryColor)}
          secondaryColor={ensureHexColor(cardPack.secondaryColor)}
          showPlayerDetails={showPlayerDetails}
        />
      )}
    </>
  );
};

PackOpeningDialog.fragments = {
  cardPack: gql`
    fragment PackOpeningDialog_cardPack on CardPack {
      slug
      pictureUrl
      price
      currency
      primaryColor: color(variant: PRIMARY)
      secondaryColor: color(variant: SECONDARY)
    }
  ` as TypedDocumentNode<PackOpeningDialog_cardPack>,
};
